import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RegisterInput } from '@lub0v/santa-utils';
import {
  AuthFrame,
  AuthSocial,
  Button,
  Checkbox,
  Divider,
  HintError,
  EmailInput,
  Link,
  OtpChallenge,
  Stack,
  Typography,
} from '@src/components';
import { useEnterOrder } from '@src/forms/useEnterOrder';
import {
  loginWithGoogle,
  loginWithTelegram,
  loginWithVk,
  registerWithEmail,
  verifyOtpCode,
} from '@src/pages/auth/auth';
import { useValidatedForm } from '@src/validation';
import { useHydrated } from '@src/utils/useHydrated';

const REGISTER_ENTER = ['register-email', 'register-consent', 'register-submit'] as const;

export function RegisterPage() {
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const form = useValidatedForm(RegisterInput, { email: '' });
  const enter = useEnterOrder(REGISTER_ENTER);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [sentEmail, setSentEmail] = useState('');

  async function run(action: () => Promise<string | null>) {
    setError(null);
    setBusy(true);
    try {
      const message = await action();
      if (message) {
        setError(message);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'register.failed');
    } finally {
      setBusy(false);
    }
  }

  async function submit(email: string) {
    setError(null);
    setBusy(true);
    try {
      const result = await registerWithEmail(email);
      if ('token' in result) {
        setSentEmail(email);
        setToken(result.token);
        return;
      }
      setError(result.error);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'register.failed');
    } finally {
      setBusy(false);
    }
  }

  if (token) {
    return (
      <AuthFrame
        title={t('otp.title')}
        testId="register-otp"
        fill
        footer={
          <Link
            variant="plain"
            testId="register-change-email"
            onClick={() => {
              setToken(null);
              setSentEmail('');
            }}
          >
            {t('otp.changeEmail')}
          </Link>
        }
      >
        <OtpChallenge
          challengeToken={token}
          testId="register-otp-code"
          onVerify={(token, code) => verifyOtpCode(navigate, token, code)}
          hint={
            <Trans
              i18nKey="register.sent"
              values={{ email: sentEmail }}
              components={{ email: <Typography as="span" role="body/strong" /> }}
            />
          }
        />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title={t('register.title')}
      subtitle={
        <Trans
          i18nKey="register.subtitle"
          components={{ login: <Link href="/login" variant="inline" /> }}
        />
      }
      testId="register"
    >
      <Stack gap={6}>
        <AuthSocial
          testId="register"
          disabled={!hydrated || busy || !consent}
          disabledHint={!consent ? t('login.consentRequired') : undefined}
          labels={{
            google: t('register.google'),
            telegram: t('register.telegram'),
            vk: t('register.vk'),
          }}
          onGoogle={() => run(() => loginWithGoogle(navigate))}
          onTelegram={() => run(() => loginWithTelegram(navigate))}
          onVk={() => run(() => loginWithVk(navigate))}
        />
        <Divider>{t('login.or')}</Divider>
        <EmailInput
          label={t('login.email')}
          autoComplete="email"
          hint={t('register.hint')}
          disabled={!hydrated}
          testId="register-email"
          {...form.field('email')}
          {...enter.bind('register-email')}
        />
        <Checkbox
          disabled={!hydrated}
          checked={consent}
          onCheckedChange={setConsent}
          testId="register-consent"
          {...enter.bind('register-consent')}
          label={
            <Trans
              i18nKey="login.consent"
              components={{
                terms: <Link href="/terms" variant="inline" />,
                privacy: <Link href="/policy" variant="inline" />,
              }}
            />
          }
        />
        <HintError error={error} testId="register-error" />
        <Button
          type="button"
          variant="primary"
          size="large"
          fullWidth
          disabled={!hydrated || busy || !consent || !form.filled}
          testId="register-submit"
          onClick={() => void form.submit((values) => submit(values.email))}
        >
          {t('register.submit')}
        </Button>
      </Stack>
    </AuthFrame>
  );
}
